// Implements: F-RPT-007, F-UIX-018
/**
 * Mockup v3 P9 Consumo "Actividad del mes" (v3 `:1806-1810`): the mockups created, the versions
 * generated and the links active now, each with its line. Pure presentation.
 *
 * "Mockups creados" compares with the previous window ("+9 frente a septiembre") once that answer
 * is known, and draws no comparison before; "Versiones generadas" gives the whole-number average per
 * mockup, hidden when no mockup was created (never a division by zero); "Enlaces activos" says how
 * many were created and revoked in the window. The active links are counted now, which the
 * section's meta says.
 *
 * Owner 2026-10-19: the head also names the window as the month so far ("Octubre hasta hoy"), and
 * the two counts of the window (mockups, versions) add the whole month before ("Septiembre
 * completo: 70") once it answered; the links counted now get no month total. The head carries the
 * section's actions ("Exportar CSV" of the window on screen), the container's.
 */

import { useId } from "react";
import type { ReactElement, ReactNode } from "react";

import { useTranslation } from "../../../shared/i18n";
import type { ActivityReportView } from "../../../types/reports";
import { capitalised, monthOf } from "../model/previousWindow";
import styles from "./ActivityTiles.module.css";

export interface ActivityTilesProps {
  readonly current: ActivityReportView;
  /** The window before, once it answered. */
  readonly previous?: ActivityReportView | undefined;
  /** The whole month before, once it answered. */
  readonly wholePrevious?: ActivityReportView | undefined;
  /** The section's actions, in its head. */
  readonly actions?: ReactNode;
}

type Translate = ReturnType<typeof useTranslation>["t"];

/** The month's Spanish name as v3 writes it ("septiembre"), from the dictionary. */
function monthName(month: number, t: Translate): string {
  const names = [
    t("reports.activity.month.january"),
    t("reports.activity.month.february"),
    t("reports.activity.month.march"),
    t("reports.activity.month.april"),
    t("reports.activity.month.may"),
    t("reports.activity.month.june"),
    t("reports.activity.month.july"),
    t("reports.activity.month.august"),
    t("reports.activity.month.september"),
    t("reports.activity.month.october"),
    t("reports.activity.month.november"),
    t("reports.activity.month.december"),
  ];
  return names[month - 1] ?? "";
}

function comparison(current: number, previous: ActivityReportView, t: Translate): string {
  const month = monthName(monthOf(previous), t);
  const delta = current - previous.mockups_created;
  if (delta > 0) return t("reports.activity.compare.up", { count: delta, month });
  if (delta < 0) return t("reports.activity.compare.down", { count: -delta, month });
  return t("reports.activity.compare.same", { month });
}

function wholeMonth(count: number, whole: ActivityReportView, t: Translate): string {
  return t("reports.activity.wholeMonth", { month: capitalised(monthName(monthOf(whole), t)), count });
}

export function ActivityTiles({ current, previous, wholePrevious, actions }: ActivityTilesProps): ReactElement {
  const { t } = useTranslation();
  const headingId = useId();
  return (
    <section className={styles.section} aria-labelledby={headingId}>
      <div className={styles.head}>
        <h3 id={headingId} className={styles.title}>
          {t("reports.activity.title")}
        </h3>
        <span className={styles.meta}>{t("reports.activity.soFar", { month: capitalised(monthName(monthOf(current), t)) })}</span>
        <span className={styles.meta}>{t("reports.activity.meta")}</span>
        {actions === undefined ? null : <div className={styles.actions}>{actions}</div>}
      </div>
      <ul className={styles.tiles} aria-label={t("reports.activity.tiles.label")}>
        <li className={styles.tile}>
          <span className={styles.label}>{t("reports.activity.mockups")}</span>
          <strong className={styles.value}>{current.mockups_created}</strong>
          {previous === undefined ? null : (
            <span className={styles.meta}>{comparison(current.mockups_created, previous, t)}</span>
          )}
          {wholePrevious === undefined ? null : (
            <span className={styles.meta}>{wholeMonth(wholePrevious.mockups_created, wholePrevious, t)}</span>
          )}
        </li>
        <li className={styles.tile}>
          <span className={styles.label}>{t("reports.activity.versions")}</span>
          <strong className={styles.value}>{current.versions_generated}</strong>
          {current.mockups_created === 0 ? null : (
            <span className={styles.meta}>
              {t("reports.activity.average", { count: Math.round(current.versions_generated / current.mockups_created) })}
            </span>
          )}
          {wholePrevious === undefined ? null : (
            <span className={styles.meta}>{wholeMonth(wholePrevious.versions_generated, wholePrevious, t)}</span>
          )}
        </li>
        <li className={styles.tile}>
          <span className={styles.label}>{t("reports.activity.links")}</span>
          <strong className={styles.value}>{current.active_links}</strong>
          <span className={styles.meta}>
            {t("reports.activity.linksMeta", { created: current.links_created, revoked: current.links_revoked })}
          </span>
        </li>
      </ul>
    </section>
  );
}
